import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { format } from 'date-fns';
import { useLocation, useNavigate } from 'react-router';

type UrlFiltersOptions<F> = {
  /** Namespaces the keys when several lists share one page. */
  prefix?: string;
  /** Keys holding a `Date` (or nothing), written as `yyyy-MM-dd`. */
  dateKeys?: (keyof F & string)[];
  /**
   * False for a hook shared with a screen that is not the list (a picker on a
   * form): it then behaves exactly like `useState`.
   */
  inUrl?: boolean;
};

const readDate = (raw: string): Date | undefined => {
  // Split rather than parsed: `new Date('2026-12-25')` is UTC midnight.
  const [year, month, day] = raw.split('-').map(Number);
  if (!year || !month || !day) return undefined;
  const parsed = new Date(year, month - 1, day);
  return isNaN(parsed.getTime()) ? undefined : parsed;
};

/** `null` means "leave the key out of the URL". */
const writeValue = (value: unknown, isDate: boolean): string | null => {
  if (value === undefined || value === null) return null;
  if (isDate && value instanceof Date) return format(value, 'yyyy-MM-dd');
  return String(value);
};

/** Each list's query string as last shown, by pathname. */
const lastListSearch = new Map<string, string>();

/** Forget every list's last filters: they can name another tenant's records. */
export const forgetListFilters = () => lastListSearch.clear();

/**
 * A list path with the filters it was last shown with, for a form that
 * returns to a fixed route instead of going back. Params already in `listPath`
 * (a tab, say) must still match, or the plain path is used.
 */
export const listPathWithFilters = (listPath: string): string => {
  const [pathname, own = ''] = listPath.split('?');
  const remembered = lastListSearch.get(pathname);
  if (!remembered) return listPath;

  const rememberedParams = new URLSearchParams(remembered);
  const matches = Array.from(new URLSearchParams(own)).every(
    ([key, value]) => rememberedParams.get(key) === value
  );
  return matches ? `${pathname}${remembered}` : listPath;
};

/**
 * Writes from one commit (a reset touching two lists, say) land in a single
 * navigation, so the second never overwrites the first.
 */
let pending: { params: URLSearchParams; flush: () => void } | null = null;

const queueWrite = (
  base: string,
  navigate: ReturnType<typeof useNavigate>,
  apply: (params: URLSearchParams) => void
) => {
  if (!pending) {
    const batch = {
      params: new URLSearchParams(base),
      flush: () => {
        pending = null;
        const search = batch.params.toString();
        navigate({ search: search ? `?${search}` : '' }, { replace: true });
      },
    };
    pending = batch;
    queueMicrotask(batch.flush);
  }
  apply(pending.params);
};

/**
 * `useState` for a list's filters, mirrored into the URL the way the bookings
 * list does it: opening a record and coming back (Back, or a form's
 * `navigate(-1)`) lands on the same filters, page and sort, while the sidebar
 * link opens the list clean. Only values that differ from `initial` are
 * written, and with `replace`, so Back leaves the list instead of stepping
 * through every filter change.
 *
 * `initial` is read once, on mount, like `useState`.
 */
export const useUrlFilters = <F extends object>(
  initial: F,
  { prefix = '', dateKeys = [], inUrl = true }: UrlFiltersOptions<F> = {}
): [F, Dispatch<SetStateAction<F>>] => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialRef = useRef(initial);
  const keys = Object.keys(initialRef.current) as (keyof F & string)[];

  const isDate = (key: keyof F & string) =>
    dateKeys.includes(key) || initialRef.current[key] instanceof Date;

  const parse = (search: string): F => {
    const params = new URLSearchParams(search);
    const out: Record<string, unknown> = {};
    keys.forEach(key => {
      const fallback = initialRef.current[key];
      const raw = params.get(prefix + key);
      out[key] = fallback;
      if (raw === null) return;
      if (isDate(key)) out[key] = raw ? readDate(raw) : undefined;
      else if (typeof fallback === 'number')
        out[key] = Number.isFinite(Number(raw)) && raw !== '' ? +raw : fallback;
      else if (typeof fallback === 'boolean') out[key] = raw === 'true';
      else if (Array.isArray(fallback)) out[key] = raw ? raw.split(',') : [];
      else out[key] = raw;
    });
    return out as F;
  };

  /** This list's own keys as they should read in the URL. */
  const ownEntries = (filters: F) =>
    keys.map(key => {
      const value = writeValue(filters[key], isDate(key));
      const base = writeValue(initialRef.current[key], isDate(key));
      // Equal to the default: leave it out. Cleared from a default: write it
      // empty, or the default would come back on the next read.
      const written = value === base ? null : (value ?? '');
      return [prefix + key, written] as const;
    });

  const ownString = (entries: ReturnType<typeof ownEntries>) =>
    entries.map(([key, value]) => `${key}=${value ?? '\u0000'}`).join('&');

  const ownStringOf = (search: string) => {
    const params = new URLSearchParams(search);
    return ownString(keys.map(key => [prefix + key, params.get(prefix + key)]));
  };

  const [filters, setFiltersState] = useState<F>(() =>
    parse(inUrl ? location.search : '')
  );

  const searchRef = useRef(location.search);
  searchRef.current = location.search;
  /** Writes sent but not yet seen in the URL. */
  const inFlight = useRef(new Set<string>());

  // State -> URL.
  useEffect(() => {
    const entries = ownEntries(filters);
    const own = ownString(entries);
    if (!inUrl || own === ownStringOf(searchRef.current)) return;
    inFlight.current.add(own);
    queueWrite(searchRef.current, navigate, params =>
      entries.forEach(([key, value]) =>
        value === null ? params.delete(key) : params.set(key, value)
      )
    );
    // Keyed on the filters alone: the helpers read refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (inUrl) lastListSearch.set(location.pathname, location.search);
  }, [inUrl, location.pathname, location.search]);

  // URL -> state, for a change this hook did not make (the sidebar link
  // reopening the list while it is already open).
  useEffect(() => {
    if (!inUrl) return;
    const own = ownStringOf(location.search);
    if (inFlight.current.has(own)) {
      // Our own write arriving; anything sent before it is settled too.
      inFlight.current.clear();
      return;
    }
    if (pending || own === ownString(ownEntries(filters))) return;
    setFiltersState(parse(location.search));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const setFilters = useCallback<Dispatch<SetStateAction<F>>>(
    next => setFiltersState(next),
    []
  );

  return [filters, setFilters];
};
